import React, { useEffect, useMemo, useState } from 'react';
// Locale formatting, and the description the add-form collects -- both were only ever rendered
// by the read-only Services block that the tab split deleted.
import { formatServicePrice } from '../services/currencyUtils';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, FormControl,
  Grid, IconButton, InputLabel, MenuItem, Select, Stack, TextField,
  Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { providersApi } from '../services/api';
import { providerImageUrl } from '../services/imageUtils';
import LocationPickerMap from './LocationPickerMap';

// Mirrors net.anipaw.provider.DogServiceType.
const SERVICE_TYPES = [
  'DOG_WALKING', 'BOARDING', 'HOUSE_SITTING', 'DROP_IN_VISITS',
  'DAY_CARE', 'TRAINING', 'GROOMING', 'STORE',
];

const formFromProvider = (provider) => ({
  title: provider.title || '',
  about: provider.about || '',
  publicEmail: provider.publicEmail || '',
  publicPhone: provider.publicPhone || '',
  publicWebsite: provider.publicWebsite || '',
  instagramUser: provider.instagramUser || '',
  businessCity: provider.businessCity || '',
  businessLat: provider.businessLat ?? '',
  businessLng: provider.businessLng ?? '',
});

const EMPTY_SERVICE = { dogServiceType: 'DOG_WALKING', price: '', currencyCode: '', description: '' };

/**
 * Admin edit surface for a provider: details, public contact, location, visibility, services
 * and images.
 *
 * Everything here goes through the /admin/providers endpoints rather than the owner/public ones,
 * for two reasons that bite specifically on seeded profiles: the owner service endpoint 403s
 * while a profile is unclaimed, and a full PUT /providers/{id} wipes images and services from
 * ProviderDto's empty defaults. The admin PUT is partial — omitted fields are left alone — so
 * this panel only ever sends what it actually edited.
 */
const ProviderAdminEditPanel = ({ provider, onUpdated, showSuccess, showError, section = 'all' }) => {
  const [form, setForm] = useState(() => formFromProvider(provider));
  const [savingDetails, setSavingDetails] = useState(false);
  const [newService, setNewService] = useState(EMPTY_SERVICE);
  const [addingService, setAddingService] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [serviceError, setServiceError] = useState('');

  // Re-seed the form whenever the provider is refetched, so a save elsewhere on the page
  // doesn't leave these inputs showing stale values.
  useEffect(() => setForm(formFromProvider(provider)), [provider]);

  const dirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(formFromProvider(provider)),
    [form, provider]
  );

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const apply = async (payload, successMessage, setBusy) => {
    setBusy(true);
    try {
      const updated = await providersApi.adminUpdateProvider(provider.id, payload);
      onUpdated(updated);
      showSuccess(successMessage);
      return true;
    } catch (e) {
      showError(`Update failed: ${e.message}`);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const handleSaveDetails = () => {
    const toNumber = (v) => (String(v).trim() === '' ? null : Number(v));
    const lat = toNumber(form.businessLat);
    const lng = toNumber(form.businessLng);
    if ((lat !== null && !Number.isFinite(lat)) || (lng !== null && !Number.isFinite(lng))) {
      showError('Latitude and longitude must be numbers');
      return;
    }
    // Empty string (not null) is the explicit "clear this field" signal to the admin endpoint.
    apply(
      {
        title: form.title,
        about: form.about,
        publicEmail: form.publicEmail,
        publicPhone: form.publicPhone,
        publicWebsite: form.publicWebsite,
        instagramUser: form.instagramUser,
        businessCity: form.businessCity,
        ...(lat !== null ? { businessLat: lat } : {}),
        ...(lng !== null ? { businessLng: lng } : {}),
      },
      'Provider details updated',
      setSavingDetails
    );
  };

  const handleAddService = async () => {
    setServiceError('');
    const price = Number(String(newService.price).trim());
    if (!Number.isFinite(price) || price <= 0) {
      // Matches ProviderEligibility.isValidService — a zero-price service never counts as valid.
      setServiceError('Price must be greater than 0');
      return;
    }
    setAddingService(true);
    try {
      const updated = await providersApi.adminAddService(provider.id, {
        dogServiceType: newService.dogServiceType,
        price,
        currencyCode: newService.currencyCode.trim() || null,
        description: newService.description.trim() || null,
      });
      onUpdated(updated);
      setNewService(EMPTY_SERVICE);
      showSuccess('Service added');
    } catch (e) {
      showError(`Failed to add service: ${e.message}`);
    } finally {
      setAddingService(false);
    }
  };

  const handleRemoveService = async (serviceId) => {
    try {
      onUpdated(await providersApi.adminRemoveService(provider.id, serviceId));
      showSuccess('Service removed');
    } catch (e) {
      showError(`Failed to remove service: ${e.message}`);
    }
  };

  const handleUploadImages = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (files.length === 0) return;
    setUploading(true);
    try {
      const names = [];
      for (const file of files) {
        // Sequential on purpose: the upload endpoint runs Vision moderation per image, and a
        // parallel burst from the admin panel is a good way to get rate-limited.
        names.push(await providersApi.uploadImage(file));
      }
      const updated = await providersApi.adminUpdateProvider(provider.id, {
        images: [...(provider.images || []), ...names.filter(Boolean)],
      });
      onUpdated(updated);
      showSuccess(names.length === 1 ? 'Image added' : `${names.length} images added`);
    } catch (e) {
      showError(`Image upload failed: ${e.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = (name) =>
    apply(
      { images: (provider.images || []).filter((image) => image !== name) },
      'Image removed',
      setUploading
    );

  // Rendered in three independently-saveable blocks so ProviderDetails can place each one
  // under the tab it belongs to: details on Overview, services on Services, images on Media.
  // Putting the whole panel under one tab would render images twice (there and on Media) and
  // bury upload/delete under a heading that says Services.
  const show = (name) => section === 'all' || section === name;

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        {provider.adminSuspended && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This provider is admin-suspended. Visibility stays off until it is unsuspended —
            the backend forces it to false on every edit.
          </Alert>
        )}

        {show('details') && (<>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Details</Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12}>
            <TextField
              label="Business name" value={form.title} fullWidth
              onChange={(e) => update('title', e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="About" value={form.about} fullWidth multiline minRows={3}
              onChange={(e) => update('about', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Email" value={form.publicEmail} fullWidth
              onChange={(e) => update('publicEmail', e.target.value)}
              helperText="Used for the claim invite and duplicate detection"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Phone" value={form.publicPhone} fullWidth
              onChange={(e) => update('publicPhone', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Instagram" value={form.instagramUser} fullWidth placeholder="@handle"
              onChange={(e) => update('instagramUser', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Website" value={form.publicWebsite} fullWidth placeholder="https://"
              onChange={(e) => update('publicWebsite', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="City" value={form.businessCity} fullWidth
              onChange={(e) => update('businessCity', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Latitude" type="number" value={form.businessLat} fullWidth
              inputProps={{ step: 'any' }}
              onChange={(e) => update('businessLat', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Longitude" type="number" value={form.businessLng} fullWidth
              inputProps={{ step: 'any' }}
              onChange={(e) => update('businessLng', e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <LocationPickerMap
              latitude={form.businessLat}
              longitude={form.businessLng}
              onLocationChange={(lat, lng) =>
                setForm((prev) => ({
                  ...prev,
                  businessLat: lat.toFixed(6),
                  businessLng: lng.toFixed(6),
                }))
              }
            />
          </Grid>
        </Grid>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button variant="contained" disabled={!dirty || savingDetails} onClick={handleSaveDetails}>
            {savingDetails ? 'Saving…' : 'Save details'}
          </Button>
          {dirty && (
            <Button color="inherit" onClick={() => setForm(formFromProvider(provider))}>
              Discard changes
            </Button>
          )}
        </Stack>

        </>)}

        {show('services') && (<>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Services</Typography>
        {provider.services?.length > 0 ? (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            {provider.services.map((service) => (
              <Chip
                key={service.id}
                label={`${service.dogServiceType} · ${formatServicePrice(service)}`}
                title={service.description || undefined}
                onDelete={() => handleRemoveService(service.id)}
                deleteIcon={<DeleteIcon />}
              />
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No services priced yet — a profile needs at least one service above 0 to be bookable.
          </Typography>
        )}
        {serviceError && <Alert severity="error" sx={{ mb: 2 }}>{serviceError}</Alert>}
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="admin-new-service-type">Type</InputLabel>
              <Select
                labelId="admin-new-service-type" label="Type" value={newService.dogServiceType}
                onChange={(e) => setNewService({ ...newService, dogServiceType: e.target.value })}
              >
                {SERVICE_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField
              size="small" label="Price" type="number" value={newService.price} fullWidth
              inputProps={{ step: 'any', min: 0 }}
              onChange={(e) => setNewService({ ...newService, price: e.target.value })}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField
              size="small" label="Currency" value={newService.currencyCode} fullWidth
              placeholder="ILS"
              onChange={(e) => setNewService({ ...newService, currencyCode: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              size="small" label="Description" value={newService.description} fullWidth
              onChange={(e) => setNewService({ ...newService, description: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button
              fullWidth variant="outlined" startIcon={<AddIcon />}
              disabled={addingService} onClick={handleAddService}
            >
              Add
            </Button>
          </Grid>
        </Grid>

        </>)}

        {show('images') && (<>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="subtitle1">Images</Typography>
          <Button
            size="small" variant="outlined" component="label"
            startIcon={uploading ? <CircularProgress size={16} /> : <UploadFileIcon />}
            disabled={uploading} sx={{ ml: 'auto' }}
          >
            {uploading ? 'Working…' : 'Add images'}
            <input hidden multiple type="file" accept="image/*" onChange={handleUploadImages} />
          </Button>
        </Stack>
        {provider.images?.length > 0 ? (
          <Grid container spacing={1}>
            {provider.images.map((name) => (
              <Grid item xs={6} sm={4} md={3} key={name}>
                <Box sx={{ position: 'relative' }}>
                  <Box
                    component="img"
                    src={providerImageUrl(name, 'medium')}
                    alt={name}
                    sx={{ width: '100%', height: 130, objectFit: 'cover', borderRadius: 1, display: 'block' }}
                  />
                  <Tooltip title="Remove image">
                    <IconButton
                      size="small"
                      disabled={uploading}
                      onClick={() => handleRemoveImage(name)}
                      sx={{
                        position: 'absolute', top: 4, right: 4,
                        bgcolor: 'background.paper',
                        '&:hover': { bgcolor: 'background.paper' },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Typography variant="body2" color="text.secondary">No images yet.</Typography>
        )}
        </>)}
      </CardContent>
    </Card>
  );
};

export default ProviderAdminEditPanel;
